import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getProfile } from '../../../services';
import { getApplyFormData } from '../../../services/applyFormService';
import { PrimaryButton, SecondaryButton } from '../../ui';

/**
 * Collaboration Application 审批模态框
 * 显示申请者信息，允许发布者同意或拒绝申请
 */
const CollaborationApplicationModal = ({
  isOpen,
  onClose,
  notification,
  onApprove,
  onDeny,
}) => {
  const navigate = useNavigate();

  // 获取申请者ID（即使 notification 为 null 也要先定义，避免 hooks 条件调用）
  // 需要从多个可能的位置获取，因为数据库可能没有正确保存
  // 注意：这个值可能在 useEffect 中被更新，所以我们需要一个状态来存储最终值
  // 优先级：meta.applicantId > senderId > null
  const [finalApplicantId, setFinalApplicantId] = useState(() => {
    const id =
      notification?.meta?.applicantId || notification?.senderId || null;
    return id;
  });

  console.log('[CollaborationApplicationModal] Extracting applicantId:', {
    fromMetaApplicantId: notification?.meta?.applicantId,
    fromSenderId: notification?.senderId,
    metaType: typeof notification?.meta,
    metaKeys: notification?.meta ? Object.keys(notification.meta) : null,
    fullMeta: notification?.meta,
    initialFinalApplicantId: finalApplicantId,
    fullNotification: notification, // 输出完整通知对象用于调试
  });

  // 状态：用于存储从数据库获取的真实用户名（必须在早期返回之前）
  const [realApplicantName, setRealApplicantName] = useState(null);
  const [realApplicantAvatar, setRealApplicantAvatar] = useState(null);

  // 如果通知中的 applicantName 是 "Anonymous" 或 applicantId 是 null，尝试从多个来源获取（必须在早期返回之前）
  useEffect(() => {
    if (!isOpen || !notification) return;

    const fetchRealUserInfo = async () => {
      // 如果 applicantId 是 null，尝试从 localStorage 的 applyForm 数据中获取
      let currentApplicantId =
        finalApplicantId ||
        notification?.meta?.applicantId ||
        notification?.senderId ||
        null;

      if (!currentApplicantId && notification.meta?.collaborationId) {
        console.log(
          '[CollaborationApplicationModal] ApplicantId is null, trying to get from localStorage applyForm data'
        );

        // 尝试从所有可能的 userId 获取 applyForm 数据
        const collaborationId = notification.meta.collaborationId;
        const storageKeyPrefix = `tag_apply_form_data_${collaborationId}_`;

        // 遍历 localStorage 查找匹配的 applyForm 数据
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(storageKeyPrefix)) {
            try {
              const data = JSON.parse(localStorage.getItem(key));
              if (data && data.userId) {
                currentApplicantId = data.userId;
                console.log(
                  '[CollaborationApplicationModal] Found applicantId from localStorage:',
                  currentApplicantId
                );
                // 更新状态
                setFinalApplicantId(currentApplicantId);
                break;
              }
            } catch (e) {
              // 忽略解析错误
            }
          }
        }
      }

      if (!currentApplicantId) {
        console.error(
          '[CollaborationApplicationModal] CRITICAL: Could not find applicantId from any source'
        );
        console.error(
          '[CollaborationApplicationModal] Full notification object for debugging:',
          {
            notification,
            meta: notification.meta,
            senderId: notification.senderId,
            senderName: notification.senderName,
            collaborationId: notification.meta?.collaborationId,
            localStorageKeys: Array.from(
              { length: localStorage.length },
              (_, i) => localStorage.key(i)
            ).filter(key => key && key.includes('tag_apply_form_data')),
          }
        );
        return;
      }

      const storedName =
        notification.meta?.applicantName || notification.senderName;

      // 如果存储的名字是 "Anonymous" 或类似的值，尝试重新获取
      if (
        !storedName ||
        storedName === 'Anonymous' ||
        storedName === 'Unknown User' ||
        storedName.startsWith('User_')
      ) {
        console.log(
          '[CollaborationApplicationModal] Stored name is invalid, fetching from database:',
          storedName,
          'applicantId:',
          currentApplicantId
        );

        try {
          const profileResult = await getProfile(currentApplicantId);
          console.log(
            '[CollaborationApplicationModal] Profile result:',
            profileResult
          );

          if (profileResult.success && profileResult.data) {
            const name =
              profileResult.data.fullName?.trim() ||
              profileResult.data.name?.trim() ||
              profileResult.data.displayName?.trim();

            if (name && name !== 'Anonymous') {
              console.log(
                '[CollaborationApplicationModal] Found real name from profile:',
                name
              );
              setRealApplicantName(name);
            }

            if (profileResult.data.avatar) {
              setRealApplicantAvatar(profileResult.data.avatar);
            }
          }
        } catch (error) {
          console.error(
            '[CollaborationApplicationModal] Failed to fetch user profile:',
            error
          );
        }
      }
    };

    fetchRealUserInfo();
  }, [isOpen, notification, finalApplicantId]);

  // 早期返回（必须在所有 Hooks 之后）
  if (!isOpen || !notification) {
    console.log('[CollaborationApplicationModal] Not rendering:', {
      isOpen,
      hasNotification: !!notification,
    });
    return null;
  }

  console.log(
    '[CollaborationApplicationModal] Rendering with notification:',
    notification
  );
  console.log('[CollaborationApplicationModal] Meta:', notification.meta);

  // 从通知的 meta 中提取申请信息（优先使用从数据库获取的真实姓名）
  const applicantName =
    realApplicantName ||
    (notification.meta?.applicantName &&
    notification.meta?.applicantName !== 'Anonymous'
      ? notification.meta.applicantName
      : null) ||
    (notification.senderName && notification.senderName !== 'Anonymous'
      ? notification.senderName
      : null) ||
    'Unknown User';

  const applicantAvatar =
    realApplicantAvatar ||
    notification.meta?.applicantAvatar ||
    notification.senderAvatar ||
    '/assets/placeholder.svg';

  console.log('[CollaborationApplicationModal] Applicant name:', applicantName);
  console.log(
    '[CollaborationApplicationModal] Applicant ID:',
    finalApplicantId
  );

  // 处理头像点击，跳转到申请者个人页面
  const handleAvatarClick = () => {
    // 按优先级获取 applicantId
    const idToUse =
      finalApplicantId ||
      notification?.meta?.applicantId ||
      notification?.senderId;

    console.log('[CollaborationApplicationModal] handleAvatarClick called:', {
      finalApplicantId,
      metaApplicantId: notification?.meta?.applicantId,
      senderId: notification?.senderId,
      idToUse,
    });

    if (idToUse) {
      console.log(
        '[CollaborationApplicationModal] Navigating to applicant profile:',
        idToUse
      );
      navigate(`/artist/${idToUse}`);
      onClose(); // 关闭模态框
    } else {
      console.error(
        '[CollaborationApplicationModal] ERROR: No applicant ID available for navigation'
      );
      console.error(
        '[CollaborationApplicationModal] Full notification object:',
        notification
      );
      // 即使没有 ID，也尝试显示错误提示
      alert(
        'Unable to navigate: Applicant ID is missing. Please check the console for details.'
      );
    }
  };
  const projectTitle =
    notification.meta?.collaborationTitle ||
    notification.meta?.projectTitle ||
    'Untitled Collaboration';
  const applicationData = notification.meta?.applicationData || {};
  const email = applicationData.email || '';
  const portfolio = applicationData.portfolio || '';
  const message = applicationData.message || '';
  const positionId = applicationData.positionId || null;

  const handleContact = () => {
    if (email) {
      try {
        const mailtoLink = `mailto:${email}`;
        window.open(mailtoLink, '_blank');
      } catch (error) {
        console.error(
          '[CollaborationApplicationModal] Failed to open mailto:',
          error
        );
        navigator.clipboard
          .writeText(email)
          .then(() => {
            alert(`Email address copied to clipboard: ${email}`);
          })
          .catch(() => {
            alert(`Please copy this email address: ${email}`);
          });
      }
    }
  };

  const handleApprove = () => {
    if (onApprove) {
      onApprove(notification);
    }
  };

  const handleDeny = () => {
    if (onDeny) {
      onDeny(notification);
    }
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between'>
          <h3 className='text-xl font-semibold text-gray-900'>
            Collaboration Application
          </h3>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 transition-colors duration-200'
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className='px-6 py-6 space-y-6'>
          {/* 申请者信息 */}
          <div className='flex items-center gap-4 pb-4 border-b border-gray-200'>
            <img
              src={applicantAvatar}
              alt={applicantName}
              className='w-16 h-16 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:opacity-80 transition-opacity duration-200'
              onClick={handleAvatarClick}
              title='Click to view profile'
            />
            <div className='flex-1'>
              <h4 className='text-xl font-semibold text-gray-900'>
                {applicantName}
              </h4>
              <p className='text-sm text-gray-500 mt-1'>
                Applied on{' '}
                {new Date(notification.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* 申请的项目信息 */}
          <div className='bg-purple-50 rounded-lg p-4 border border-purple-200'>
            <div className='flex items-start gap-3'>
              <div className='flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center'>
                <svg
                  className='w-6 h-6 text-purple-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
                  />
                </svg>
              </div>
              <div className='flex-1'>
                <h5 className='text-sm font-medium text-purple-900 mb-1'>
                  Project
                </h5>
                <p className='text-base font-semibold text-purple-800'>
                  {projectTitle}
                </p>
              </div>
            </div>
          </div>

          {/* 申请的职位信息（如果有） */}
          {positionId && (
            <div className='bg-blue-50 rounded-lg p-4 border border-blue-200'>
              <div className='flex items-start gap-3'>
                <div className='flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-6 h-6 text-blue-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                    />
                  </svg>
                </div>
                <div className='flex-1'>
                  <h5 className='text-sm font-medium text-blue-900 mb-1'>
                    Open Position
                  </h5>
                  <p className='text-base font-semibold text-blue-800'>
                    Position ID: {positionId}
                  </p>
                  <p className='text-sm text-blue-600 mt-1'>
                    (Position details will be shown after approval)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 申请者详细信息 */}
          <div className='space-y-4'>
            <h5 className='text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2'>
              Application Details
            </h5>

            {/* Email */}
            {email && (
              <div>
                <div className='block text-sm font-medium text-gray-700 mb-1'>
                  Email
                </div>
                <a
                  href={`mailto:${email}`}
                  className='text-purple-600 hover:text-purple-700 underline text-sm'
                  onClick={e => {
                    e.preventDefault();
                    handleContact();
                  }}
                >
                  {email}
                </a>
              </div>
            )}

            {/* Portfolio */}
            {portfolio && (
              <div>
                <div className='block text-sm font-medium text-gray-700 mb-1'>
                  Portfolio
                </div>
                <a
                  href={
                    portfolio.startsWith('http')
                      ? portfolio
                      : `https://${portfolio}`
                  }
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-purple-600 hover:text-purple-700 underline break-all text-sm'
                >
                  {portfolio}
                </a>
              </div>
            )}

            {/* Message */}
            {message && (
              <div>
                <div className='block text-sm font-medium text-gray-700 mb-2'>
                  Message
                </div>
                <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
                  <p className='text-gray-900 whitespace-pre-wrap text-sm'>
                    {message}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className='sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3'>
          <button
            onClick={handleDeny}
            className='flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors duration-200'
          >
            Deny
          </button>
          <PrimaryButton
            onClick={handleApprove}
            className='flex-1 bg-green-600 hover:bg-green-700'
          >
            Approve
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default CollaborationApplicationModal;
